import { PRACTICE_SKILLS } from "@ca/shared";
import type { Space as TagSpace } from "@ca/ui";
import { HIDDEN, hiddenPath, featureOn, type Feature } from "./features.ts";
/** Bốn khu có menu riêng; Hôm nay, Blog, Góp ý, Pro, Cài đặt thuộc khu chung. */
export type Space = "study" | "gram" | "prod" | "note";
export const spaces: { key: Space; label: string; icon: string; home: string; tip: string }[] = [
  {
    key: "study",
    label: "Làm bài",
    icon: "📝",
    home: "/study",
    tip: `Làm bài|đề thi · luyện part${featureOn("dictation") ? " · nghe & chép" : ""} · lỗi sai · điểm`,
  },
  {
    key: "gram",
    label: "Ngữ pháp",
    icon: "📚",
    home: "/grammar",
    tip: "Ngữ pháp|lý thuyết Part 5–6 · từ vựng · ôn đúng lúc sắp quên",
  },
  {
    key: "prod",
    label: "Năng suất",
    icon: "⏱",
    home: "/report",
    tip: `Năng suất|giờ học · tập trung · lịch · thói quen${featureOn("game") ? " · game giờ nghỉ" : ""}`,
  },
  {
    key: "note",
    label: "Take note",
    icon: "📒",
    home: "/notes",
    tip: "Take note|ghi chép theo 4 phương pháp · ghi nhanh · mọi ghi chú",
  },
];
/**
 * Số đếm bên phải mục menu (`.cnt`), lấy từ dữ liệu thật:
 * ets/mock số đề · errors nhóm lỗi đang trị · score điểm full test gần nhất · due từ đến hạn ôn ·
 * pomo hiệp hôm nay/4 · hab thói quen xong/tổng · notes ghi chú/giới hạn · nb:<cách ghi> số trang.
 */
export type Count =
  "ets" | "mock" | "errors" | "score" | "due" | "pomo" | "hab" | "notes" | `nb:${string}`;
export interface NavItem {
  label: string;
  icon: string;
  path: string;
  count?: Count;
  /** Hiện huy hiệu MỚI (bật/tắt chung bằng cờ `SHOW_NEW_BADGES`). */
  isNew?: boolean;
}
export type NavEntry =
  NavItem | { group: string } | { action: "note"; label: string; icon: string };
export const NOTE_METHODS = [
  ["cornell", "Cornell", "📑", "Phương pháp Cornell"],
  ["box", "Đóng hộp", "🔲", "Phương pháp đóng hộp"],
  ["map", "Bản đồ", "🗺️", "Phương pháp bản đồ"],
  ["mind", "Sơ đồ tư duy", "🧠", "Phương pháp sơ đồ tư duy"],
  ["plain", "Vở bình thường", "📓", "Quyển vở bình thường"],
] as const;
const fullNavigation: Record<Space, NavEntry[]> = {
  study: [
    { label: "Tổng quan", icon: "◎", path: "/study" },
    { group: "Luyện đề" },
    { label: "Đề thi ETS", icon: "📝", path: "/tests", count: "ets" },
    { label: "Đề thi thử", icon: "📄", path: "/mocks", count: "mock" },
    ...PRACTICE_SKILLS.map((s) => ({ label: s.label, icon: s.icon, path: `/practice/${s.key}` })),
    { label: "Nghe & chép", icon: "🎧", path: "/dictation", isNew: true },
    { group: "Ôn & trị lỗi" },
    { label: "Sổ tay lỗi sai", icon: "📕", path: "/errors", count: "errors" },
    { group: "Kết quả" },
    { label: "Bảng điểm", icon: "📈", path: "/scores", count: "score" },
  ],
  gram: [
    { label: "Lý thuyết", icon: "📖", path: "/grammar", isNew: true },
    { label: "Từ vựng", icon: "📗", path: "/vocab", count: "due" },
  ],
  prod: [
    { label: "Báo cáo giờ học", icon: "📊", path: "/report", isNew: true },
    { label: "Tập trung", icon: "⏱", path: "/focus", count: "pomo" },
    { group: "Kế hoạch" },
    { label: "Lịch học tuần", icon: "📅", path: "/plan" },
    { label: "Thói quen", icon: "✅", path: "/habits", count: "hab" },
    { group: "Của bạn" },
    { label: "Thành tích", icon: "🫧", path: "/rewards", isNew: true },
    { group: "Giờ nghỉ" },
    { label: "Game giờ nghỉ", icon: "🎮", path: "/game", isNew: true },
  ],
  note: [
    { label: "Tất cả ghi chú", icon: "📒", path: "/notes", count: "notes" },
    { action: "note", label: "Ghi nhanh", icon: "🗒️" },
    { group: "Phương pháp" },
    ...NOTE_METHODS.map(([key, label, icon]) => ({
      label,
      icon,
      path: `/notebook/${key}`,
      count: `nb:${key}` as const,
    })),
  ],
};
const isItem = (e: NavEntry): e is NavItem => "path" in e;
/** Menu sau khi bỏ trang đang ẩn; nhóm không còn mục nào cũng bỏ theo. */
export function visibleNavigation(hidden: ReadonlySet<Feature>): Record<Space, NavEntry[]> {
  const keep = (entries: NavEntry[]) => {
    const items = entries.filter((e) => !isItem(e) || !hiddenPath(e.path, hidden));
    return items.filter((e, i) => {
      const next = items[i + 1];
      return !("group" in e) || (next !== undefined && !("group" in next));
    });
  };
  return Object.fromEntries(Object.entries(fullNavigation).map(([k, v]) => [k, keep(v)])) as Record<
    Space,
    NavEntry[]
  >;
}
export const navigation = visibleNavigation(HIDDEN);
/** Tên màn hiển thị ở thanh trên (giống `SCREENS_META` của bản mẫu). */
const TITLES: Record<string, string> = {
  "/": "Hôm nay",
  "/blog": "Blog",
  "/feedback": "Góp ý",
  "/settings": "Cài đặt",
  "/pro": "Nâng cấp Pro",
  "/notebook/cornell": "Phương pháp Cornell",
  "/notebook/box": "Phương pháp đóng hộp",
  "/notebook/map": "Phương pháp bản đồ",
  "/notebook/mind": "Phương pháp sơ đồ tư duy",
  "/notebook/plain": "Quyển vở bình thường",
};
const COMMON = ["/", "/blog", "/feedback", "/settings", "/pro"];
/** Mọi trang có thể mở từ menu, dùng cho ô tìm nhanh. */
export function allPages() {
  return [
    ...spaces.flatMap((s) => navigation[s.key].filter(isItem)),
    ...COMMON.map((path) => ({ label: TITLES[path] ?? path, path })),
  ];
}
export function pageMeta(path: string): {
  label: string;
  path: string;
  /** Khu của menu trái: màn chung giữ khu đang mở trước đó, nên trả về null. */
  space: Space | null;
  tag: TagSpace;
} {
  if (path.startsWith("/attempts/"))
    return { label: "Màn làm bài", path, space: "study", tag: "study" };
  for (const space of spaces) {
    const entry = navigation[space.key]
      .filter(isItem)
      .find((n) => path === n.path || path.startsWith(`${n.path}/`));
    if (entry)
      return { label: TITLES[entry.path] ?? entry.label, path, space: space.key, tag: space.key };
  }
  if (path.startsWith("/notes/"))
    return { label: "Tất cả ghi chú", path, space: "note", tag: "note" };
  const common = COMMON.find((p) => (p === "/" ? path === "/" : path.startsWith(p)));
  return {
    label: common ? (TITLES[common] ?? "") : "Không tìm thấy trang",
    path,
    space: null,
    tag: "common",
  };
}
